import React, { useState, useEffect } from "react";
import {
  View,
  Text,
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Switch,
  TextInput,
} from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { DropdownSource, MappingFieldType } from "../../../types/ticketingConfig.types";
import styles, { optStyles, rowStyles, skeletonStyles } from "./AdditionalFieldsModal.style";

// ── Types ─────────────────────────────────────────────────────────────────────

export type FieldType = MappingFieldType;

export type Field = {
  id: string;
  backendId?: number;
  fieldName: string;
  fieldLabel: string;
  type: FieldType;
  required: boolean;
  charLimit: string;
  dropdownSource: DropdownSource | "";
  placeholder: string;
  tooltip: string;
  options: string[];
};

type AdditionalFieldsModalProps = {
  visible: boolean;
  onClose: () => void;
  onSave: (fields: Field[]) => void;
  onDeleteField?: (field: Field) => Promise<void> | void;
  subtitle?: string;
  initialFields?: Field[];
  loading?: boolean;
  maxFields?: number;
  title?: string;
  saveError?: string | null;
  readOnly?: boolean;
};

// ── Helpers ───────────────────────────────────────────────────────────────────

let _idSeq = 1;
function genId() {
  return "f" + _idSeq++;
}

function makeEmptyField(): Field {
  return {
    id: genId(),
    fieldName: "",
    fieldLabel: "",
    type: "text",
    required: false,
    charLimit: "",
    dropdownSource: "",
    placeholder: "",
    tooltip: "",
    options: [],
  };
}

const EMPTY_FIELDS: Field[] = [];
const FIELD_TITLE_MAX_LENGTH = 30;

function isCharLimitVisible(type: FieldType) {
  return type === "text" || type === "textarea" || type === "text_multi" || type === "text_url" || type === "email_multi";
}

function isPlaceholderVisible(type: FieldType) {
  return type === "text" || type === "textarea" || type === "text_multi" || type === "text_url" || type === "email_multi";
}

function isDropdownSourceVisible(type: FieldType) {
  return type === "single_select" || type === "single_select_searchable" || isMultiSelectType(type);
}

function isMultiSelectType(type: FieldType) {
  return type === "multi_select" || type === "multi_select_searchable";
}

function getFieldTypeLabel(type: FieldType) {
  switch (type) {
    case "text":
      return "Text";
    case "textarea":
      return "Text Area";
    case "text_multi":
      return "Multi Text";
    case "text_url":
      return "URL Text";
    case "email_multi":
      return "Multi Email";
    case "single_select":
      return "Single Select";
    case "single_select_searchable":
      return "Single Select (Searchable)";
    case "multi_select":
      return "Multi Select";
    case "multi_select_searchable":
      return "Multi Select (Searchable)";
    case "auto_populated":
      return "Auto-Populated";
    case "file_upload":
      return "File Upload";
    default:
      return type;
  }
}

function getDropdownSourceOptions(type: FieldType): DropdownSource[] {
  if (type === "single_select") {
    return ["static_options", "navigator_module_api"];
  }
  if (type === "single_select_searchable") {
    return ["static_options", "navigator_location_api"];
  }
  if (isMultiSelectType(type)) {
    return ["static_options"];
  }
  return [];
}

function supportsOptionsEditor(type: FieldType, dropdownSource: DropdownSource | "") {
  return isDropdownSourceVisible(type) && dropdownSource === "static_options";
}

function getDuplicateOption(field: Field) {
  if (!supportsOptionsEditor(field.type, field.dropdownSource)) {
    return null;
  }

  const seen = new Set<string>();
  for (const option of field.options || []) {
    const normalizedOption = option.trim().toLowerCase();
    if (seen.has(normalizedOption)) {
      return option.trim() || option;
    }
    seen.add(normalizedOption);
  }

  return null;
}

function normalizeFieldTitle(value: string) {
  return value.trim().toLowerCase();
}

function hasDuplicateFieldTitle(field: Field, fields: Field[]) {
  const normalizedTitle = normalizeFieldTitle(field.fieldLabel);
  return Boolean(normalizedTitle) && fields.filter(
    (candidate) => normalizeFieldTitle(candidate.fieldLabel) === normalizedTitle
  ).length > 1;
}

function getComparableField(field: Field) {
  return {
    fieldName: field.fieldName,
    fieldLabel: field.fieldLabel,
    type: field.type,
    required: field.required,
    charLimit: field.charLimit,
    dropdownSource: field.dropdownSource,
    placeholder: field.placeholder,
    tooltip: field.tooltip,
    options: field.options,
  };
}

function areFieldListsEqual(left: Field[], right: Field[]) {
  if (left.length !== right.length) {
    return false;
  }

  return left.every((field, index) => {
    const leftComparable = getComparableField(field);
    const rightComparable = getComparableField(right[index]);
    return JSON.stringify(leftComparable) === JSON.stringify(rightComparable);
  });
}

// ── Option chip editor (shown inline when type === "dropdown") ────────────────

const OptionsEditor: React.FC<{
  field: Field;
  onChange: (options: string[]) => void;
  duplicateOption?: string | null;
  readOnly?: boolean;
}> = ({ field, onChange, duplicateOption, readOnly = false }) => {
  const [optInput, setOptInput] = useState("");
  const [optionError, setOptionError] = useState<string | null>(null);

  const addOption = () => {
    const val = optInput.trim();
    if (!val) {
      return;
    }
    if ((field.options || []).some((o) => o.trim().toLowerCase() === val.toLowerCase())) {
      setOptionError(`Duplicate option values are not allowed: ${val}`);
      return;
    }
    onChange([...(field.options || []), val]);
    setOptInput("");
    setOptionError(null);
  };

  const removeOption = (idx: number) => {
    const next = [...(field.options || [])];
    next.splice(idx, 1);
    onChange(next);
  };

  return (
    <View style={optStyles.wrap}>
      <Text style={optStyles.label}>Dropdown Options</Text>
      <View style={optStyles.chipList}>
        {(field.options || []).length === 0 ? (
          <Text style={optStyles.empty}>No options yet</Text>
        ) : (
          (field.options || []).map((opt, i) => (
            <View key={i} style={optStyles.chip}>
              <Text style={optStyles.chipText}>{opt}</Text>
              <Pressable style={optStyles.chipRemove} onPress={() => removeOption(i)} disabled={readOnly}>
                <MaterialIcons name="close" size={11} color="#818181" />
              </Pressable>
            </View>
          ))
        )}
      </View>
      {duplicateOption ? (
        <Text style={rowStyles.errorText}>Duplicate option values are not allowed: {duplicateOption}</Text>
      ) : null}
      {!readOnly ? (
        <View style={optStyles.addRow}>
          <TextInput
            style={optStyles.addInput}
            placeholder="Add option and press Enter"
            placeholderTextColor="#A8A8A8"
            value={optInput}
            onChangeText={(value) => {
              setOptInput(value);
              setOptionError(null);
            }}
            onSubmitEditing={addOption}
            returnKeyType="done"
          />
          <Pressable style={optStyles.addBtn} onPress={addOption}>
            <Text style={optStyles.addBtnText}>Add Option</Text>
          </Pressable>
        </View>
      ) : null}
      {optionError ? <Text style={rowStyles.errorText}>{optionError}</Text> : null}
    </View>
  );
};

// ── Field row ─────────────────────────────────────────────────────────────────

const FIELD_TYPE_OPTIONS: Array<{ value: FieldType; label: string }> = [
  { value: "text", label: "Text" },
  { value: "textarea", label: "Text Area" },
  { value: "text_url", label: "URL Text" },
  { value: "email_multi", label: "Multi Email" },
  { value: "single_select", label: "Single Select" },
  { value: "single_select_searchable", label: "Single Select (Searchable)" },
  { value: "multi_select", label: "Multi Select" },
  { value: "multi_select_searchable", label: "Multi Select (Searchable)" },
];

const FieldRow: React.FC<{
  field: Field;
  index: number;
  onUpdate: (updated: Field) => void;
  onDelete: () => void;
  deleting?: boolean;
  duplicateFieldTitle?: boolean;
  readOnly?: boolean;
}> = ({ field, index, onUpdate, onDelete, deleting = false, duplicateFieldTitle = false, readOnly = false }) => {
  return (
    <View style={rowStyles.wrap}>
      <View style={rowStyles.top}>
        {/* Order badge */}
        <View style={rowStyles.badge}>
          <Text style={rowStyles.badgeText}>{index + 1}</Text>
        </View>

        <View style={rowStyles.headerTextWrap}>
          <Text style={rowStyles.headerTitle}>{field.fieldLabel || "New field"}</Text>
        </View>

        <View style={rowStyles.headerBadges}>
          <View style={rowStyles.typeBadge}>
            <Text style={rowStyles.typeBadgeText}>{getFieldTypeLabel(field.type)}</Text>
          </View>
        </View>

        {/* Delete */}
        <Pressable style={[rowStyles.deleteBtn, deleting && rowStyles.deleteBtnDisabled]} onPress={onDelete} disabled={readOnly || deleting}>
          <MaterialIcons name="delete-outline" size={17} color="#d32f2f" />
        </Pressable>
      </View>

      <View style={rowStyles.body}>
        <View style={rowStyles.fieldBlock}>
          <Text style={rowStyles.fieldLabel}>
            Field Title <Text style={rowStyles.requiredMark}>*</Text>
          </Text>
          <TextInput
            style={rowStyles.textInput}
            placeholder="Dashboard Link"
            placeholderTextColor="#A8A8A8"
            value={field.fieldLabel}
            editable={!readOnly}
            maxLength={FIELD_TITLE_MAX_LENGTH}
            onChangeText={(value) => onUpdate({ ...field, fieldLabel: value })}
          />
          {duplicateFieldTitle ? (
            <Text style={rowStyles.errorText}>Duplicate Field Title. Please use a unique title.</Text>
          ) : null}
        </View>

        <View style={rowStyles.fieldBlock}>
          <Text style={rowStyles.fieldLabel}>Field Type</Text>
          <View style={rowStyles.typeWrap}>
            {FIELD_TYPE_OPTIONS.map((option) => (
              <Pressable
                key={option.value}
                style={[rowStyles.typeChip, field.type === option.value && rowStyles.typeChipActive]}
                onPress={() => {
                  const nextType = option.value;
                  const nextDropdownSource = getDropdownSourceOptions(nextType).includes(field.dropdownSource as DropdownSource)
                    ? field.dropdownSource
                    : getDropdownSourceOptions(nextType)[0] || "";
                  onUpdate({
                    ...field,
                    type: nextType,
                    options: supportsOptionsEditor(nextType, nextDropdownSource) ? field.options : [],
                    dropdownSource: isDropdownSourceVisible(nextType) ? nextDropdownSource : "",
                    placeholder: isPlaceholderVisible(nextType) ? field.placeholder : "",
                    charLimit: isCharLimitVisible(nextType) ? field.charLimit : "",
                  });
                }}
                disabled={readOnly}
              >
                <Text style={[rowStyles.typeChipText, field.type === option.value && rowStyles.typeChipTextActive]}>
                  {option.label}
                </Text>
              </Pressable>
            ))}
          </View>
        </View>

        <View style={rowStyles.requiredControl}>
          <Text style={rowStyles.fieldLabel}>Required</Text>
          <Switch
            value={field.required}
            onValueChange={(required) => onUpdate({ ...field, required })}
            disabled={readOnly}
            trackColor={{ false: "#D0D0D0", true: "#9FC1ED" }}
            thumbColor={field.required ? "#034895" : "#F4F4F4"}
          />
        </View>

        <View style={rowStyles.inlineRow}>
          {isCharLimitVisible(field.type) ? (
            <View style={[rowStyles.fieldBlock, rowStyles.inlineField]}> 
              <Text style={rowStyles.fieldLabel}>Char Limit</Text>
              <TextInput
                style={rowStyles.textInput}
                placeholder="500"
                placeholderTextColor="#A8A8A8"
                keyboardType="number-pad"
                value={field.charLimit}
                onChangeText={(value) => onUpdate({ ...field, charLimit: value.replace(/[^0-9]/g, "") })}
              />
            </View>
          ) : null}

          {isPlaceholderVisible(field.type) ? (
            <View style={[rowStyles.fieldBlock, rowStyles.inlineField]}>
              <Text style={rowStyles.fieldLabel}>Placeholder</Text>
              <TextInput
                style={rowStyles.textInput}
                placeholder="Paste the URL here"
                placeholderTextColor="#A8A8A8"
                value={field.placeholder}
                onChangeText={(value) => onUpdate({ ...field, placeholder: value })}
              />
            </View>
          ) : null}
        </View>

        {isDropdownSourceVisible(field.type) ? (
          <View style={rowStyles.fieldBlock}>
            <Text style={rowStyles.fieldLabel}>Dropdown Source</Text>
            <View style={rowStyles.typeWrap}>
              {getDropdownSourceOptions(field.type).map((source) => (
                <Pressable
                  key={source}
                  style={[
                    rowStyles.typeChip,
                    field.dropdownSource === source && rowStyles.typeChipActive,
                  ]}
                  onPress={() =>
                    onUpdate({
                      ...field,
                      dropdownSource: source,
                      options: source === "static_options" ? field.options : [],
                    })
                  }
                  disabled={readOnly}
                >
                  <Text
                    style={[
                      rowStyles.typeChipText,
                      field.dropdownSource === source && rowStyles.typeChipTextActive,
                    ]}
                  >
                    {source.replace(/_/g, " ")}
                  </Text>
                </Pressable>
              ))}
            </View>
          </View>
        ) : null}

        {supportsOptionsEditor(field.type, field.dropdownSource) ? (
          <OptionsEditor
            field={field}
            onChange={(options) => onUpdate({ ...field, options })}
            duplicateOption={getDuplicateOption(field)}
            readOnly={readOnly}
          />
        ) : null}

        <View style={rowStyles.fieldBlock}>
          <Text style={rowStyles.fieldLabel}>Tooltip</Text>
          <TextInput
            style={[rowStyles.textInput, rowStyles.textArea]}
            placeholder="Help text shown beside the field"
            placeholderTextColor="#A8A8A8"
            value={field.tooltip}
            multiline
            numberOfLines={3}
            editable={!readOnly}
            onChangeText={(value) => onUpdate({ ...field, tooltip: value })}
          />
        </View>
      </View>
    </View>
  );
};

const SkeletonRow: React.FC<{ index: number }> = ({ index }) => {
  return (
    <View style={skeletonStyles.row}>
      <View style={skeletonStyles.badge}>
        <Text style={skeletonStyles.badgeText}>{index + 1}</Text>
      </View>
      <View style={[skeletonStyles.block, skeletonStyles.fieldTitle]} />
      <View style={[skeletonStyles.block, skeletonStyles.typeSelect]} />
      <View style={[skeletonStyles.block, skeletonStyles.requiredSelect]} />
      <View style={[skeletonStyles.block, skeletonStyles.deleteBtn]} />
    </View>
  );
};

// ── Main modal ────────────────────────────────────────────────────────────────

const AdditionalFieldsModal: React.FC<AdditionalFieldsModalProps> = ({
  visible,
  onClose,
  onSave,
  onDeleteField,
  subtitle = "—",
  initialFields = EMPTY_FIELDS,
  loading = false,
  maxFields = 10,
  title = "Additional Fields",
  saveError = null,
  readOnly = false,
}) => {
  const [fields, setFields] = useState<Field[]>(initialFields);
  const [baselineFields, setBaselineFields] = useState<Field[]>(initialFields);
  const [saveBanner, setSaveBanner] = useState<string | null>(null);
  const [deletingFieldId, setDeletingFieldId] = useState<string | null>(null);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);

  // Sync when the modal opens and when the async field query updates its data.
  useEffect(() => {
    if (!visible) {
      return;
    }

    setFields(initialFields.map((f) => ({ ...f })));
    setBaselineFields(initialFields.map((f) => ({ ...f })));
    setSaveBanner(null);
    setDeletingFieldId(null);
    setShowDiscardDialog(false);
  }, [visible, initialFields]);

  useEffect(() => {
    if (!saveError) {
      setSaveBanner(null);
      return;
    }

    setSaveBanner(saveError);
  }, [saveError]);

  const canAddMore = fields.length < maxFields;
  const showLoadingSkeleton = loading;
  const hasUnsavedChanges = !areFieldListsEqual(fields, baselineFields);
  const hasMissingRequiredValues = fields.some((field) => !field.fieldLabel.trim());
  const hasDuplicateFieldTitles = fields.some((field) => hasDuplicateFieldTitle(field, fields));
  const hasDuplicateOptions = fields.some((field) => Boolean(getDuplicateOption(field)));
  const isSaveDisabled =
    !hasUnsavedChanges ||
    hasMissingRequiredValues ||
    hasDuplicateFieldTitles ||
    hasDuplicateOptions ||
    showLoadingSkeleton ||
    deletingFieldId !== null;

  const handleAddField = () => {
    if (!canAddMore) return;
    setFields((prev) => [...prev, makeEmptyField()]);
  };

  const handleUpdate = (id: string, updated: Field) => {
    setFields((prev) => prev.map((f) => (f.id === id ? updated : f)));
    setSaveBanner(null);
  };

  const handleDelete = (id: string) => {
    setFields((prev) => prev.filter((f) => f.id !== id));
    setBaselineFields((prev) => prev.filter((f) => f.id !== id));
  };

  const handleDeleteField = async (field: Field) => {
    if (!onDeleteField) {
      handleDelete(field.id);
      return;
    }

    setDeletingFieldId(field.id);
    setSaveBanner(null);

    try {
      await Promise.resolve(onDeleteField(field));
      handleDelete(field.id);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to disable field";
      setSaveBanner(message);
    } finally {
      setDeletingFieldId(null);
    }
  };

  const handleSave = async () => {
    if (isSaveDisabled || hasDuplicateOptions) {
      return;
    }
    await Promise.resolve(onSave(fields));
  };

  const handleClose = () => {
    setFields(initialFields.map((f) => ({ ...f })));
    setBaselineFields(initialFields.map((f) => ({ ...f })));
    setSaveBanner(null);
    setDeletingFieldId(null);
    setShowDiscardDialog(false);
    onClose();
  };

  const requestClose = () => {
    if (hasUnsavedChanges) {
      setShowDiscardDialog(true);
      return;
    }

    handleClose();
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={requestClose}
    >
      <View style={styles.overlay}>
        <Pressable style={StyleSheet.absoluteFillObject} onPress={requestClose} />
        <View style={styles.modal}>
          {/* Header */}
          <View style={styles.header}>
            <View style={{ flex: 1 }}>
              <Text style={styles.title}>{title}</Text>
              <Text style={styles.subtitle}>{subtitle}</Text>
            </View>
            <Pressable onPress={requestClose} style={styles.closeButton}>
              <MaterialIcons name="close" size={18} color="#034895" />
            </Pressable>
          </View>

          {/* Body */}
          <View style={styles.body}>
            {/* Toolbar: count + add button */}
            <View style={styles.toolbar}>
              <Text style={styles.count}>
                {fields.length} / {maxFields} additional fields
              </Text>
              <Pressable
                style={[styles.addButton, (readOnly || !canAddMore || showLoadingSkeleton) && styles.addButtonDisabled]}
                onPress={handleAddField}
                disabled={readOnly || !canAddMore || showLoadingSkeleton}
              >
                <MaterialIcons
                  name="add"
                  size={13}
                  color={canAddMore && !showLoadingSkeleton ? "#034895" : "#A8A8A8"}
                />
                <Text
                  style={[
                    styles.addButtonText,
                    (readOnly || !canAddMore || showLoadingSkeleton) && styles.addButtonTextDisabled,
                  ]}
                >
                  Add Fields
                </Text>
              </Pressable>
            </View>

            {saveBanner ? (
              <View style={styles.errorBanner}>
                <MaterialIcons name="error-outline" size={16} color="#B42318" />
                <Text style={styles.errorBannerText}>{saveBanner}</Text>
              </View>
            ) : null}

            {/* Field list */}
            <ScrollView
              style={styles.fieldsList}
              contentContainerStyle={styles.fieldsListContent}
              keyboardShouldPersistTaps="handled"
            >
              {showLoadingSkeleton ? (
                <View style={styles.skeletonWrap}>
                  <View style={styles.skeletonHeader}>
                    <View style={[styles.skeletonLine, styles.skeletonHeaderLine]} />
                    <View style={styles.skeletonChip} />
                  </View>
                  <SkeletonRow index={0} />
                  <SkeletonRow index={1} />
                  <SkeletonRow index={2} />
                </View>
              ) : fields.length === 0 ? (
                <View style={styles.emptyState}>
                  <Text style={styles.emptyText}>
                    No additional fields yet. These appear on the Submit Ticket form when this category / subcategory / tertiary is selected.
                  </Text>
                </View>
              ) : (
                fields.map((field, idx) => (
                  <FieldRow
                    key={field.id}
                    field={field}
                    index={idx}
                    onUpdate={(updated) => handleUpdate(field.id, updated)}
                    onDelete={() => handleDeleteField(field)}
                    deleting={deletingFieldId === field.id}
                    duplicateFieldTitle={hasDuplicateFieldTitle(field, fields)}
                    readOnly={readOnly}
                  />
                ))
              )}
            </ScrollView>
          </View>

          {/* Footer */}
          <View style={styles.footer}>
            <Pressable style={[styles.button, styles.cancelButton]} onPress={requestClose}>
              <Text style={styles.cancelButtonText}>Cancel</Text>
            </Pressable>
            {!readOnly ? (
              <Pressable
                style={[styles.button, styles.saveButton, isSaveDisabled && styles.saveButtonDisabled]}
                onPress={handleSave}
                disabled={isSaveDisabled}
              >
                <Text style={styles.saveButtonText}>Save</Text>
              </Pressable>
            ) : null}
          </View>
        </View>

        {showDiscardDialog ? (
          <View style={styles.dialogBackdrop}>
            <View style={styles.dialog}>
              <Text style={styles.dialogTitle}>Unsaved Changes</Text>
              <Text style={styles.dialogMessage}>You have unsaved changes. Discard and close?</Text>
              <View style={styles.dialogActions}>
                <Pressable style={[styles.dialogButton, styles.dialogButtonGhost]} onPress={() => setShowDiscardDialog(false)}>
                  <Text style={styles.dialogButtonGhostText}>Keep Editing</Text>
                </Pressable>
                <Pressable style={[styles.dialogButton, styles.dialogButtonDanger]} onPress={handleClose}>
                  <Text style={styles.dialogButtonDangerText}>Discard Changes</Text>
                </Pressable>
              </View>
            </View>
          </View>
        ) : null}
      </View>
    </Modal>
  );
};

export default AdditionalFieldsModal;
